export type UserRole = 'referee' | 'student' | 'admin' | 'developer';

export interface User {
  id: number;
  username: string;
  reference_no: string;
  full_name?: string | null;
  role: UserRole;
  email?: string | null;
  photo_path?: string | null;
  photo_url?: string | null;
}

export const EVENT_TYPES = [
  'decision',
  'goal',
  'foul',
  'timeout',
  'yellow_card',
  'red_card',
  'blue_card',
  'advantage',
  'play_on',
  'goal_kick',
  'free_throw',
  'throw_in',
  'seven_meter',
  'kickoff',
  'opposite_free_throw',
  'verbal_warning',
  'two_minutes',
  'no_penalty',
  'steps',
  'double',
  'charging',
  'kicking',
  'goal_area_violation',
  'blocking',
  'offensive_foul',
  'pivot',
  'tripping',
  'pushing_in_air',
  'team_timeout',
  'bench_foul',
  'long_holding',
  'pull_down',
  'face_hit',
  'goalie_head_hit',
  'jersey_pull',
  'diving',
  'empty_goal',
  'last_30_seconds',
  'note',
  'other',
] as const;

export type EventType = (typeof EVENT_TYPES)[number];

export const EVENT_TYPE_LABELS: Record<EventType, string> = {
  decision: '判決',
  goal: '入球',
  foul: '犯規',
  timeout: '暫停',
  yellow_card: '黃牌',
  red_card: '紅牌',
  blue_card: '藍牌',
  advantage: '有利',
  play_on: 'Play On',
  goal_kick: '龍門球',
  free_throw: '自由球',
  throw_in: '界外球',
  seven_meter: '七米球',
  kickoff: '中圈開球',
  opposite_free_throw: '對隊自由球',
  verbal_warning: '口頭警告',
  two_minutes: '兩分鐘',
  no_penalty: '無罰則',
  steps: '走步',
  double: 'Double',
  charging: '撞人',
  kicking: 'Kicking',
  goal_area_violation: '越區',
  blocking: '閂閘',
  offensive_foul: '進攻犯規',
  pivot: '中鋒',
  tripping: '攝腳',
  pushing_in_air: '空中推人',
  team_timeout: '隊暫停',
  bench_foul: '替補犯規',
  long_holding: 'Long Holding',
  pull_down: 'Pull Down',
  face_hit: '打面',
  goalie_head_hit: '龍門爆頭',
  jersey_pull: '拉衫',
  diving: '插水',
  empty_goal: '空門',
  last_30_seconds: '最後30秒',
  note: '備註',
  other: '其他',
};

export const ON_FIELD_BALL_DECISION_OPTIONS = [
  'play_on',
  'goal_kick',
  'free_throw',
  'throw_in',
  'seven_meter',
  'opposite_free_throw',
] as const;

export type OnFieldBallDecisionOption = (typeof ON_FIELD_BALL_DECISION_OPTIONS)[number];

export const REVIEWED_BALL_DECISION_OPTIONS = [
  'play_on',
  'goal_kick',
  'free_throw',
  'throw_in',
  'seven_meter',
  'opposite_free_throw',
] as const;

export type ReviewedBallDecisionOption = (typeof REVIEWED_BALL_DECISION_OPTIONS)[number];

export const SANCTION_OPTIONS = [
  'verbal_warning',
  'yellow_card',
  'two_minutes',
  'red_card',
  'blue_card',
  'no_penalty',
] as const;

export type SanctionOption = (typeof SANCTION_OPTIONS)[number];

export const ON_FIELD_PUNISHMENT_OPTIONS = SANCTION_OPTIONS;
export type OnFieldPunishmentOption = SanctionOption;

export const REVIEWED_PUNISHMENT_OPTIONS = SANCTION_OPTIONS;
export type ReviewedPunishmentOption = SanctionOption;

/** @deprecated Use ON_FIELD_BALL_DECISION_OPTIONS */
export const BALL_DECISION_OPTIONS = ON_FIELD_BALL_DECISION_OPTIONS;
export type BallDecisionOption = OnFieldBallDecisionOption;

/** @deprecated Use SANCTION_OPTIONS */
export const PUNISHMENT_OPTIONS = SANCTION_OPTIONS;
export type PunishmentOption = SanctionOption;

export const ON_FIELD_BALL_DECISION_LABELS: Record<OnFieldBallDecisionOption, string> = {
  play_on: 'Play On',
  goal_kick: '龍門球',
  free_throw: '自由球',
  throw_in: '界外球',
  seven_meter: '七米球',
  opposite_free_throw: '對隊自由球',
};

/** Legacy stored values — hidden from pickers but still shown in lists/exports. */
export const LEGACY_BALL_DECISION_LABELS: Record<string, string> = {
  kickoff: '中圈開球',
};

export const REVIEWED_BALL_DECISION_LABELS: Record<ReviewedBallDecisionOption, string> = {
  play_on: 'Play On',
  goal_kick: '龍門球',
  free_throw: '自由球',
  throw_in: '界外球',
  seven_meter: '七米球',
  opposite_free_throw: '對隊自由球',
};

export const PARTICIPANT_DEADLINE_MESSAGE =
  '已過截止時間，無法新增標記。如有需要請聯絡管理員。';

export const SANCTION_LABELS: Record<SanctionOption, string> = {
  verbal_warning: '口頭警告',
  yellow_card: '黃牌',
  two_minutes: '兩分鐘',
  red_card: '紅牌',
  blue_card: '藍牌',
  no_penalty: '無罰則',
};

export const ON_FIELD_PUNISHMENT_LABELS = SANCTION_LABELS;
export const REVIEWED_PUNISHMENT_LABELS = SANCTION_LABELS;

/** @deprecated Use ON_FIELD_BALL_DECISION_LABELS */
export const BALL_DECISION_LABELS = ON_FIELD_BALL_DECISION_LABELS;

/** @deprecated Use ON_FIELD_PUNISHMENT_LABELS */
export const PUNISHMENT_LABELS = ON_FIELD_PUNISHMENT_LABELS;

export const DECISION_OPTIONS = [
  'goal',
  'no_goal',
  'foul',
  'no_foul',
  'yellow_card',
  'red_card',
  'blue_card',
  'timeout',
  'advantage',
  'free_throw',
  'seven_meter',
  'other',
] as const;

export type DecisionOption = (typeof DECISION_OPTIONS)[number];

export const DECISION_LABELS: Record<DecisionOption, string> = {
  goal: '入球',
  no_goal: '無入球',
  foul: '犯規',
  no_foul: '無犯規',
  yellow_card: '黃牌',
  red_card: '紅牌',
  blue_card: '藍牌',
  timeout: '暫停',
  advantage: '有利',
  free_throw: '罰球',
  seven_meter: '七米球',
  other: '其他',
};

export const JUDGEMENT_OPTIONS = ['correct', 'wrong', 'uncertain'] as const;
export type JudgementOption = (typeof JUDGEMENT_OPTIONS)[number];

export const JUDGEMENT_LABELS: Record<JudgementOption, string> = {
  correct: '正確',
  wrong: '錯誤',
  uncertain: '灰色',
};

export const JUDGEMENT_UNCERTAIN_COLOR = '#546e7a';
export const JUDGEMENT_UNCERTAIN_COLOR_LIGHT = '#90a4ae';

export const JUDGEMENT_COLORS: Record<JudgementOption, 'success' | 'error' | 'warning' | 'default'> = {
  correct: 'success',
  wrong: 'error',
  uncertain: 'default',
};

export const PERIODS = ['1H', '2H', 'OT1_1H', 'OT1_2H', 'OT2_1H', 'OT2_2H', 'S7M'] as const;
export type Period = (typeof PERIODS)[number];

export const PERIOD_LABELS: Record<Period, string> = {
  '1H': '上半場',
  '2H': '下半場',
  OT1_1H: '加時一（上）',
  OT1_2H: '加時一（下）',
  OT2_1H: '加時二（上）',
  OT2_2H: '加時二（下）',
  S7M: '七米球決勝',
};

export const DEFAULT_HOME_COLOR = '#8bc34a';
export const DEFAULT_AWAY_COLOR = '#2196f3';

export const TEAM_COLOR_PRESETS = [
  '#8bc34a',
  '#2196f3',
  '#f44336',
  '#ff9800',
  '#ffeb3b',
  '#9c27b0',
  '#ffffff',
  '#212121',
  '#00bcd4',
  '#795548',
];

export const ASSIGNMENT_MODES = ['personal', 'educational', 'exam'] as const;
export type AssignmentMode = (typeof ASSIGNMENT_MODES)[number];

export const ASSIGNMENT_MODE_LABELS: Record<AssignmentMode, string> = {
  personal: '個人分析',
  educational: '教學分享',
  exam: '考核測驗',
};

/** Current user's relationship to a review session (functional, from share permissions). */
export type SessionMyRole = 'owner' | 'manager' | 'participant' | 'viewer';

export const SESSION_ROLE_LABELS: Record<SessionMyRole, string> = {
  owner: '建立者',
  manager: '管理員',
  participant: '參與者',
  viewer: '唯讀',
};

export function getSessionRoleLabel(role?: SessionMyRole | string | null): string {
  if (!role) return '—';
  return SESSION_ROLE_LABELS[role as SessionMyRole] || role;
}

export function roleFromSessionAccess(
  access?: Pick<
    SessionAccess,
    | 'is_owner'
    | 'is_participant'
    | 'is_viewer'
    | 'can_edit_events'
    | 'can_view_share_analytics'
  > | null
): SessionMyRole | null {
  if (!access) return null;
  if (access.is_owner) return 'owner';
  if (access.is_viewer) return 'viewer';
  const canManageMarks =
    !!access.can_edit_events ||
    !!(access.can_view_share_analytics && access.is_participant && !access.is_viewer);
  if (canManageMarks && access.is_participant) return 'manager';
  if (access.is_participant) return 'participant';
  return null;
}

export type SharePermission = 'participant' | 'viewer';

export const SHARE_PERMISSION_LABELS: Record<SharePermission, string> = {
  participant: '可備註',
  viewer: '唯讀',
};

export interface SessionAccess {
  can_view: boolean;
  can_edit: boolean;
  can_mark_official: boolean;
  can_mark_participant: boolean;
  can_share: boolean;
  can_view_analytics: boolean;
  can_view_share_analytics: boolean;
  can_view_official_marks: boolean;
  can_release_official: boolean;
  can_edit_events: boolean;
  is_owner: boolean;
  is_participant: boolean;
  is_viewer: boolean;
  share_permission?: SharePermission;
  share_id?: string | null;
  group_id?: string | null;
  official_released_at?: string | null;
  official_source_user_id?: number | null;
  official_source_user_ids?: number[];
  due_at?: string | null;
  participant_deadline_passed?: boolean;
}

/** Group managers may edit any mark; supports APIs before `can_edit_events` existed. */
export function canEditAllSessionEvents(
  access?: Pick<
    SessionAccess,
    'can_edit_events' | 'can_view_share_analytics' | 'is_participant' | 'is_viewer'
  > | null
): boolean {
  if (!access) return false;
  if (access.can_edit_events) return true;
  return !!(
    access.can_view_share_analytics &&
    access.is_participant &&
    !access.is_viewer
  );
}

export interface ReviewSession {
  id: string;
  title: string;
  competition_name?: string | null;
  home_team?: string | null;
  away_team?: string | null;
  home_team_color?: string | null;
  away_team_color?: string | null;
  match_date?: string | null;
  youtube_url?: string | null;
  youtube_video_id?: string | null;
  notes?: string | null;
  status: string;
  assignment_mode?: AssignmentMode | string;
  official_released_at?: string | null;
  created_by_user_id: number;
  created_at: string;
  updated_at: string;
  can_edit?: boolean;
  can_delete?: boolean;
  is_shared?: boolean;
  is_group_share?: boolean;
  /** When access is via a group share, use for review detail links */
  share_group_id?: string | null;
  share_group_name?: string | null;
  my_role?: SessionMyRole | null;
  access?: SessionAccess;
}

export interface LiveSession {
  id: string;
  title: string;
  competition_name?: string | null;
  home_team?: string | null;
  away_team?: string | null;
  match_date?: string | null;
  status: string;
  clock_started_at?: string | null;
  clock_paused_at?: string | null;
  accumulated_seconds: number;
  current_period: string;
  match_duration_minutes?: number;
  current_match_second?: number;
  notes?: string | null;
  created_by_user_id: number;
  created_at: string;
  updated_at: string;
  can_edit?: boolean;
}

export interface SessionEvent {
  id: string;
  session_id: string;
  session_type: 'review' | 'live';
  event_type: string;
  note?: string | null;
  team_side?: string | null;
  video_second?: number | null;
  match_second?: number | null;
  period?: string | null;
  marker_role?: 'official' | 'participant';
  parent_event_id?: string | null;
  on_field_decision?: string | null;
  on_field_punishment?: string | null;
  reviewed_decision?: string | null;
  reviewed_punishment?: string | null;
  judgement?: JudgementOption | string | null;
  created_by_user_id: number;
  created_at: string;
  updated_at: string;
  user?: { reference_no?: string; full_name?: string | null; photo_path?: string | null };
}

export interface EventResponse {
  id: string;
  session_id: string;
  official_event_id: string;
  user_id: number;
  on_field_decision?: string | null;
  on_field_punishment?: string | null;
  reviewed_decision?: string | null;
  reviewed_punishment?: string | null;
  judgement?: JudgementOption | string | null;
  note?: string | null;
  created_at: string;
  updated_at: string;
  user?: { reference_no: string; full_name?: string | null; photo_path?: string | null };
}

export interface UserAssertion {
  assertion_id: string;
  source: 'event' | 'response' | 'quick';
  source_id: string;
  linked_event_id?: string | null;
  userId: string;
  userName: string;
  photoPath?: string | null;
  role: 'owner' | 'participant';
  timestamp: number;
  onFieldBall: string;
  onFieldSanction: string;
  judgement?: 'correct' | 'incorrect' | 'uncertain' | 'pending';
  reviewedBall?: string;
  reviewedSanction?: string;
  notes?: string;
}

export interface ConsolidatedEventCluster {
  clusterId: string;
  anchorTimestamp: number;
  onFieldBall: string;
  assertions: UserAssertion[];
  dominantSanction?: string;
  agreementRate?: number;
  /** Share of assertions matching the dominant full decision signature */
  fullAgreementRate?: number;
  dominantSignature?: string;
  averageVariance?: number;
  anchorEventId?: string | null;
}

export interface SessionShare {
  id: string;
  session_id: string;
  share_type: 'user' | 'group';
  target_user_id?: number | null;
  target_group_id?: string | null;
  target_user?: MainAppUser | null;
  target_group_name?: string | null;
  permission: 'participant' | 'viewer';
  message?: string | null;
  due_at?: string | null;
  official_released_at?: string | null;
  official_source_user_id?: number | null;
  official_source_user_ids?: number[];
  official_source_user_name?: string | null;
  official_source_users?: Array<{
    user_id: number;
    full_name?: string | null;
    reference_no?: string;
  }>;
  created_by_user_id: number;
  created_at: string;
}

export type GroupMemberRole = 'owner' | 'manager' | 'participant';

export const GROUP_ROLE_LABELS: Record<GroupMemberRole, string> = {
  owner: '建立者',
  manager: '管理員',
  participant: '參與者',
};

export interface ShareGroup {
  id: string;
  name: string;
  description?: string | null;
  created_by_user_id: number;
  created_at: string;
  updated_at: string;
  my_role?: GroupMemberRole | null;
  can_manage?: boolean;
  member_count?: number;
  members_preview?: Array<{
    user_id: number;
    reference_no: string;
    full_name?: string | null;
    role: string;
    group_role?: string;
  }>;
  members?: Array<{
    user_id: number;
    reference_no: string;
    full_name?: string | null;
    role: string;
    email?: string | null;
    group_role?: string;
    added_at?: string | null;
  }>;
}

export interface GroupSharedSession {
  share_id: string;
  session_id: string;
  title: string;
  home_team?: string | null;
  away_team?: string | null;
  match_date?: string | null;
  assignment_mode: string;
  permission: string;
  due_at?: string | null;
  message?: string | null;
  shared_at: string;
  official_event_count: number;
  my_marker_count: number;
  is_overdue?: boolean;
  official_released_at?: string | null;
  official_source_user_id?: number | null;
  official_source_user_ids?: number[];
  official_source_user_name?: string | null;
  can_edit_share?: boolean;
  session_created_by_user_id?: number;
  /** Present only when the viewer is a group manager */
  participant_progress?: {
    total: number;
    responded: number;
  };
}

export interface MainAppUser {
  id: number;
  reference_no: string;
  full_name?: string | null;
  role: string;
  email?: string | null;
  photo_path?: string | null;
  photo_url?: string | null;
}

export interface AnalyticsEventStats {
  total: number;
  correct: number;
  wrong: number;
  uncertain: number;
  unset: number;
  agreement_rate: number | null;
}

export interface ReviewAnalytics {
  session: ReviewSession;
  events: Array<{
    event: SessionEvent;
    responses: EventResponse[];
    stats: AnalyticsEventStats;
  }>;
  participant_events: SessionEvent[];
  participants: Array<{
    user_id: number;
    reference_no: string;
    full_name?: string | null;
    role: string;
    response_count: number;
    marker_count: number;
  }>;
  summary: {
    official_events: number;
    total_responses: number;
    participant_markers: number;
    participant_count: number;
  };
}

export function formatVideoTime(seconds: number): string {
  const m = Math.floor(seconds / 60);
  const s = Math.floor(seconds % 60);
  return `${m}:${String(s).padStart(2, '0')}`;
}

export function formatMatchClock(seconds: number): string {
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  return `${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
}

export function getDecisionLabel(value?: string | null): string {
  if (!value) return '—';
  return (
    LEGACY_BALL_DECISION_LABELS[value] ||
    ON_FIELD_BALL_DECISION_LABELS[value as OnFieldBallDecisionOption] ||
    ON_FIELD_PUNISHMENT_LABELS[value as OnFieldPunishmentOption] ||
    REVIEWED_BALL_DECISION_LABELS[value as ReviewedBallDecisionOption] ||
    REVIEWED_PUNISHMENT_LABELS[value as ReviewedPunishmentOption] ||
    DECISION_LABELS[value as DecisionOption] ||
    value
  );
}

export function formatDecisionSummary(
  ball?: string | null,
  punishment?: string | null
): string {
  const parts: string[] = [];
  if (ball) parts.push(getDecisionLabel(ball));
  if (punishment) parts.push(getDecisionLabel(punishment));
  return parts.length ? parts.join(' · ') : '—';
}

export function getJudgementLabel(value?: string | null): string {
  if (!value) return '—';
  if (value === 'incorrect') return JUDGEMENT_LABELS.wrong;
  return JUDGEMENT_LABELS[value as JudgementOption] || value;
}

export function getJudgementTextColor(value?: string | null, dark?: boolean): string {
  const normalized =
    value === 'correct'
      ? 'correct'
      : value === 'wrong' || value === 'incorrect'
        ? 'wrong'
        : value === 'uncertain'
          ? 'uncertain'
          : null;
  if (normalized === 'correct') return dark ? 'success.light' : 'success.main';
  if (normalized === 'wrong') return dark ? 'error.light' : 'error.main';
  if (normalized === 'uncertain') {
    return dark ? JUDGEMENT_UNCERTAIN_COLOR_LIGHT : JUDGEMENT_UNCERTAIN_COLOR;
  }
  return dark ? 'rgba(255,255,255,0.65)' : 'text.secondary';
}

/** Solid fill colors for timeline event dots. */
export function getJudgementDotColor(value?: string | null, hideAnswer = false): string {
  if (hideAnswer) return '#9e9e9e';
  if (value === 'correct') return '#2e7d32';
  if (value === 'wrong' || value === 'incorrect') return '#c62828';
  return '#9e9e9e';
}

export const MAIN_APP_URL = import.meta.env.VITE_MAIN_APP_URL || 'https://www.hkhbref.com';
export const MAIN_APP_LOGIN_URL = `${MAIN_APP_URL.replace(/\/$/, '')}/login`;
export const MAIN_APP_DASHBOARD_URL = `${MAIN_APP_URL.replace(/\/$/, '')}/dashboard`;
export const MAIN_APP_API_URL = (
  import.meta.env.VITE_MAIN_API_URL ||
  (import.meta.env.DEV ? '/main-api' : 'https://api.hkhbref.com/api')
).replace(/\/$/, '');
